import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Product photo gallery (prototype). The main photo is a horizontal scroll-snap track, so phones
 * get native swipe; thumbnails (desktop) and arrows/dots (phones) move it. The expand button
 * opens a full-screen <dialog> (Esc closes it, focus returns to the button). Choosing a variant
 * shows its photo.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track] - Scroll-snap track of main photos.
 * @property {HTMLElement} status - Live region announcing "Photo N of M".
 * @property {HTMLDialogElement} [lightbox] - Full-screen view.
 * @property {HTMLImageElement} [lightboxImage] - Image in the full-screen view.
 *
 * @extends {Component<Refs>}
 */
class ProductGallery extends Component {
  requiredRefs = ['status'];

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #index = 0;

  /** @type {HTMLElement | null} */
  #lightboxOpener = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.#index = Number(this.dataset.activeIndex) || 0;
    // Show the selected variant's photo without animating.
    this.#scrollTo(this.#index, 'instant');

    // Track which photo is in view after a swipe.
    const { track } = this.refs;
    if (track) {
      this.#observer?.disconnect();
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              this.#setActive(this.#slides.indexOf(/** @type {HTMLElement} */ (entry.target)), { announce: true });
            }
          }
        },
        { root: track, threshold: 0.6 }
      );
      for (const slide of this.#slides) this.#observer.observe(slide);
    }

    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });
    this.refs.lightbox?.addEventListener('close', this.#handleLightboxClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#listeners = null;
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.hl-gallery__slide')]);
  }

  previous() {
    const count = this.#slides.length;
    this.select((this.#index - 1 + count) % count);
  }

  next() {
    this.select((this.#index + 1) % this.#slides.length);
  }

  /** @param {Event} event */
  selectFromControl(event) {
    const control = event.target instanceof Element ? event.target.closest('[data-index]') : null;
    if (!(control instanceof HTMLElement)) return;
    this.select(Number(control.dataset.index));
  }

  /**
   * Shows the photo at `index`.
   * @param {number} index
   */
  select(index) {
    if (index < 0 || index >= this.#slides.length) return;
    this.#scrollTo(index, 'smooth');
    this.#setActive(index, { announce: true });
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #scrollTo(index, behavior) {
    const { track } = this.refs;
    const slide = this.#slides[index];
    if (!track || !slide) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const left = slide.offsetLeft - track.offsetLeft;
    track.scrollTo({ left: document.dir === 'rtl' ? -left : left, behavior: reduceMotion ? 'instant' : behavior });
  }

  /**
   * @param {number} index
   * @param {{ announce?: boolean }} [options]
   */
  #setActive(index, { announce = false } = {}) {
    if (index < 0 || index === this.#index) {
      if (index >= 0) this.#syncControls(index);
      return;
    }
    this.#index = index;
    this.#syncControls(index);
    if (announce) {
      const template = this.dataset.photoTemplate ?? '';
      this.refs.status.textContent = template.replace('[index]', String(index + 1));
    }
  }

  /** @param {number} index */
  #syncControls(index) {
    this.#slides.forEach((slide, i) => slide.toggleAttribute('aria-hidden', i !== index));
    for (const control of this.querySelectorAll('.hl-gallery__thumb, .hl-gallery__dot')) {
      if (!(control instanceof HTMLElement)) continue;
      const isActive = Number(control.dataset.index) === index;
      control.setAttribute('aria-pressed', String(isActive));
      if (isActive && control.classList.contains('hl-gallery__dot')) {
        control.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
  }

  /** @param {Event} event */
  openLightbox(event) {
    const { lightbox, lightboxImage } = this.refs;
    const slide = this.#slides[this.#index];
    if (!lightbox || !lightboxImage || !slide) return;

    const image = slide.querySelector('img');
    lightboxImage.src = slide.dataset.full ?? image?.currentSrc ?? '';
    lightboxImage.alt = image?.alt ?? '';
    // Real dimensions keep the photo's shape while the full-size file loads.
    if (image?.getAttribute('width')) lightboxImage.width = Number(image.getAttribute('width'));
    if (image?.getAttribute('height')) lightboxImage.height = Number(image.getAttribute('height'));
    this.#lightboxOpener = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    lightbox.showModal();
    document.documentElement.style.overflow = 'hidden';
  }

  closeLightbox() {
    this.refs.lightbox?.close();
  }

  /** Clicking the dark backdrop (not the photo) closes the full-screen view. @param {MouseEvent} event */
  handleLightboxClick(event) {
    if (event.target === this.refs.lightbox) this.closeLightbox();
  }

  #handleLightboxClose = () => {
    document.documentElement.style.overflow = '';
    this.#lightboxOpener?.focus();
  };

  /** Shows the newly selected variant's photo. @param {Event} event */
  #handleProductSelect = (event) => {
    const promise = /** @type {{ promise?: Promise<{ detail?: { html?: Document } }> }} */ (event).promise;
    if (!(event.target instanceof Element) || event.target.closest('product-card') || !promise) return;

    promise
      .then(({ detail }) => {
        const next = detail?.html?.querySelector('hl-product-gallery');
        const mediaId = next instanceof HTMLElement ? next.dataset.variantMediaId : '';
        if (!mediaId) return;
        const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index >= 0 && index !== this.#index) this.select(index);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[hl-product-gallery]', error);
      });
  };
}

if (!customElements.get('hl-product-gallery')) {
  customElements.define('hl-product-gallery', ProductGallery);
}
